import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageCircle, Search, Users, Image, Send, Building2, 
  Package, Tag, Phone, Eye, X, Check, Loader2, FileText, Edit3
} from 'lucide-react';
import { customersAPI, whatsappMarketingAPI, packagesAPI } from '../../services/api';
import { useTenantBranding } from '../../context/TenantBrandingContext';
import toast from 'react-hot-toast';

const WhatsappMarketing = () => {
  const { labNameAr: tenantLabName } = useTenantBranding();
  // Recipients
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [searching, setSearching] = useState(false);

  // Message content
  const [, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [labName, setLabName] = useState(tenantLabName || 'المختبر');
  const [customMessage, setCustomMessage] = useState('');
  const [labPhone, setLabPhone] = useState('');

  // Package selection
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [manualPackageName, setManualPackageName] = useState('');
  const [manualPackagePrice, setManualPackagePrice] = useState('');
  const [loadingPackages, setLoadingPackages] = useState(false);

  // Sending
  const [sending, setSending] = useState(false);

  // Load packages on mount
  useEffect(() => {
    const fetchPackages = async () => {
      setLoadingPackages(true);
      try {
        const response = await packagesAPI.getAll();
        const pkgs = response.data?.data?.items || response.data?.data || [];
        setPackages(Array.isArray(pkgs) ? pkgs : []);
      } catch (err) {
        console.error('Failed to load packages:', err);
      }
      setLoadingPackages(false);
    };
    fetchPackages();
  }, []);

  // Search customers
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await customersAPI.getAll({ search: query, limit: 10 });
      const customers = response.data?.data?.items || response.data?.data || [];
      setSearchResults(Array.isArray(customers) ? customers : []);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    }
    setSearching(false);
  }, []);

  // Debounced search
  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    const timeoutId = setTimeout(() => {
      handleSearch(value);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  // Select customer
  const handleSelectCustomer = (customer) => {
    if (!selectedCustomers.find(c => c._id === customer._id)) {
      setSelectedCustomers([...selectedCustomers, customer]);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  // Remove customer
  const handleRemoveCustomer = (customerId) => {
    setSelectedCustomers(selectedCustomers.filter(c => c._id !== customerId));
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear image
  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  // Get current package info
  const getPackageInfo = () => {
    if (isManualEntry) {
      return { name: manualPackageName, price: manualPackagePrice };
    }
    if (selectedPackage) {
      return { name: selectedPackage.name_ar || selectedPackage.name, price: `${selectedPackage.price} ريال` };
    }
    return { name: '', price: '' };
  };

  // Send campaign
  const handleSend = async () => {
    if (!sendToAll && selectedCustomers.length === 0) {
      toast.error('يرجى اختيار مستلمين أو تفعيل الإرسال لجميع العملاء');
      return;
    }

    const pkgInfo = getPackageInfo();
    if (!pkgInfo.name) {
      toast.error('يرجى اختيار باقة أو إدخال اسم الباقة');
      return;
    }

    setSending(true);
    try {
      // Note: WhatsApp API only supports public URLs for images, not base64
      // Base64 images from local upload are not supported
      const payload = {
        lab_name: labName,
        package_name: pkgInfo.name,
        package_price: pkgInfo.price,
        lab_phone: labPhone,
        custom_message: customMessage,
        send_to_all: sendToAll,
        customer_ids: selectedCustomers.map(c => c._id),
        image_url: null // Images require public URL hosting
      };
      
      const response = await whatsappMarketingAPI.sendCampaign(payload);
      const result = response.data?.data || response.data;
      
      toast.success(`تم إرسال الرسالة بنجاح إلى ${result.success || (sendToAll ? 'جميع العملاء' : selectedCustomers.length)} عميل`);
      
      // Reset form
      setSelectedCustomers([]);
      setSendToAll(false);
      setImage(null);
      setImagePreview(null);
      setManualPackageName('');
      setManualPackagePrice('');
      setSelectedPackage(null);
      setCustomMessage('');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'حدث خطأ في إرسال الرسالة';
      toast.error(errorMessage);
    }
    setSending(false);
  };

  const pkgInfo = getPackageInfo();

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Header */}
      <div className="bg-gradient-to-l from-blue-600 to-blue-700 rounded-2xl p-6 mb-8 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <MessageCircle className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">التسويق عبر واتساب</h1>
            <p className="text-blue-100">إرسال حملات تسويقية للعملاء</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Form */}
        <div className="space-y-6">
          {/* Recipients Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
          >
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-blue-600" />
              اختيار المستلمين
            </h2>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={onSearchChange}
                placeholder="ابحث بالاسم أو الجوال أو رقم الهوية..."
                className="w-full pr-12 pl-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              
              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((customer) => (
                    <button
                      key={customer._id}
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full px-4 py-3 text-right hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-0"
                    >
                      <div>
                        <p className="font-medium text-gray-800">{customer.full_name}</p>
                        <p className="text-sm text-gray-500">
                          {customer.phone} • {customer.national_id}
                        </p>
                      </div>
                      <Check className="w-5 h-5 text-blue-500" />
                    </button>
                  ))}
                </div>
              )}
              
              {searching && (
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                </div>
              )}
            </div>

            {/* Send to All */}
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors mb-4">
              <input
                type="checkbox"
                checked={sendToAll}
                onChange={(e) => setSendToAll(e.target.checked)}
                className="w-5 h-5 text-blue-600 rounded"
              />
              <span className="text-gray-700">إرسال لجميع العملاء</span>
            </label>

            {/* Selected Customers */}
            {selectedCustomers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedCustomers.map((customer) => (
                  <span
                    key={customer._id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm"
                  >
                    {customer.full_name}
                    <button
                      onClick={() => handleRemoveCustomer(customer._id)}
                      className="hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <p className="text-sm text-gray-500 mt-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              {sendToAll ? 'سيتم الإرسال لجميع العملاء' : `${selectedCustomers.length} عميل محدد`}
            </p>
          </motion.div>

          {/* Message Content Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
          >
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
              <MessageCircle className="w-5 h-5 text-blue-600" />
              محتوى الرسالة
            </h2>

            {/* Image Upload - Note: Currently for preview only */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Image className="w-4 h-4" />
                صورة العرض (للمعاينة فقط)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  اختيار صورة
                </label>
                {imagePreview && (
                  <button
                    onClick={clearImage}
                    className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    حذف
                  </button>
                )}
              </div>
              <p className="text-xs text-amber-600 mt-2">
                ⚠️ الصور تتطلب رابط URL عام. حالياً يتم إرسال الرسالة النصية فقط.
              </p>
            </div>

            {/* Lab Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                اسم المختبر
              </label>
              <input
                type="text"
                value={labName}
                onChange={(e) => setLabName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Package Selection Toggle */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Package className="w-4 h-4" />
                الباقة
              </label>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => { setIsManualEntry(false); setManualPackageName(''); setManualPackagePrice(''); }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    !isManualEntry 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  اختيار من الباقات
                </button>
                <button
                  onClick={() => { setIsManualEntry(true); setSelectedPackage(null); }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    isManualEntry 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Edit3 className="w-4 h-4" />
                  إدخال يدوي
                </button>
              </div>

              {!isManualEntry ? (
                // Package Dropdown
                <select
                  value={selectedPackage?._id || ''}
                  onChange={(e) => {
                    const pkg = packages.find(p => p._id === e.target.value);
                    setSelectedPackage(pkg || null);
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">اختر باقة...</option>
                  {loadingPackages ? (
                    <option disabled>جاري التحميل...</option>
                  ) : (
                    packages.map((pkg) => (
                      <option key={pkg._id} value={pkg._id}>
                        {pkg.name_ar || pkg.name} - {pkg.price} ريال
                      </option>
                    ))
                  )}
                </select>
              ) : (
                // Manual Entry
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      value={manualPackageName}
                      onChange={(e) => setManualPackageName(e.target.value)}
                      placeholder="اسم الباقة"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={manualPackagePrice}
                      onChange={(e) => setManualPackagePrice(e.target.value)}
                      placeholder="السعر (مثال: 299 ريال)"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Custom Message */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                نص إضافي (اختياري)
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="أضف نص مخصص للرسالة..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Lab Phone */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                رقم جوال المختبر
              </label>
              <input
                type="text"
                value={labPhone}
                onChange={(e) => setLabPhone(e.target.value)}
                placeholder="05XXXXXXXX"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                dir="ltr"
              />
            </div>

            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
              {sending ? 'جاري الإرسال...' : 'إرسال الرسالة'}
            </button>
          </motion.div>
        </div>

        {/* Right Column - Preview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
        >
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-blue-600" />
            معاينة الرسالة
          </h2>

          {/* WhatsApp Chat Preview */}
          <div 
            className="rounded-2xl overflow-hidden"
            style={{ 
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23d4d4d4" fill-opacity="0.15"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              backgroundColor: '#eae6df'
            }}
          >
            {/* Chat Header */}
            <div className="bg-[#075e54] text-white px-4 py-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">{labName || 'اسم المختبر'}</p>
                <p className="text-xs text-green-200">متصل الآن</p>
              </div>
            </div>

            {/* Messages Area */}
            <div className="p-4 min-h-[400px]">
              {/* Message Bubble */}
              <div className="flex justify-start mb-4">
                <div className="max-w-[85%]">
                  {/* Image */}
                  {imagePreview && (
                    <div className="bg-white rounded-t-2xl rounded-br-2xl shadow-sm overflow-hidden mb-1">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full max-h-48 object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Message Content */}
                  <div 
                    className="bg-white rounded-2xl shadow-sm p-4 relative"
                    style={{ borderTopLeftRadius: imagePreview ? '0' : '16px' }}
                  >
                    {/* Triangle */}
                    {!imagePreview && (
                      <div 
                        className="absolute -right-2 top-0 w-4 h-4 bg-white"
                        style={{ 
                          clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                        }}
                      />
                    )}
                    
                    <div className="text-gray-800 text-sm leading-relaxed whitespace-pre-line" dir="rtl">
                      {labName && (
                        <p className="font-bold mb-2">🏥 {labName}</p>
                      )}
                      
                      {pkgInfo.name && (
                        <p className="mb-1">📦 <span className="font-semibold">{pkgInfo.name}</span></p>
                      )}
                      
                      {pkgInfo.price && (
                        <p className="mb-1">💰 السعر: <span className="font-semibold">{pkgInfo.price}</span></p>
                      )}
                      
                      {customMessage && (
                        <p className="mt-3 pt-2 border-t border-gray-100">{customMessage}</p>
                      )}
                      
                      {labPhone && (
                        <p className="mt-3">📞 للحجز والاستفسار: <span dir="ltr">{labPhone}</span></p>
                      )}
                    </div>
                    
                    {/* Timestamp */}
                    <div className="flex justify-end mt-2">
                      <span className="text-[10px] text-gray-400">
                        {new Date().toLocaleTimeString('ar-SA-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })}
                        <span className="ml-1 text-blue-500">✓✓</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Input Area (decorative) */}
            <div className="bg-[#f0f0f0] px-4 py-3 flex items-center gap-3">
              <div className="flex-1 bg-white rounded-full px-4 py-2 text-sm text-gray-400">
                اكتب رسالة...
              </div>
              <div className="w-10 h-10 rounded-full bg-[#25d366] flex items-center justify-center">
                <Send className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhatsappMarketing;
